import { useState, useEffect, useRef } from 'react';
import Collapse from 'react-bootstrap/Collapse';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import '../../styles/DateTimePickerCal.scss';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DOW_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MINUTE_OPTIONS = [0, 15, 30, 45];

// Same layout as a physical clock face: (hour, left, top) in a 220x220 dial.
const CLOCK_POSITIONS = [
  { h: 12, left: 92, top: 14 },
  { h: 1, left: 131, top: 25 },
  { h: 2, left: 160, top: 53 },
  { h: 3, left: 170, top: 92 },
  { h: 4, left: 160, top: 131 },
  { h: 5, left: 131, top: 160 },
  { h: 6, left: 92, top: 170 },
  { h: 7, left: 53, top: 160 },
  { h: 8, left: 25, top: 131 },
  { h: 9, left: 14, top: 92 },
  { h: 10, left: 25, top: 53 },
  { h: 11, left: 53, top: 25 },
];

const pad2 = (n) => (n < 10 ? `0${n}` : `${n}`);

const to12Hour = (h24) => {
  const h = h24 % 12;
  return h === 0 ? 12 : h;
};

const formatTrigger = (date) => {
  if (!date) return null;
  const dow = date.toLocaleDateString('en-US', { weekday: 'short' });
  const day = date.getDate();
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const year = date.getFullYear();
  return `${dow}, ${day} ${month} ${year}  ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
};

function buildDayCells(year, month) {
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = Array(firstDow).fill(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  return cells;
}

/**
 * Combined date + time picker: a mini month calendar for the day, then an
 * analog clock dial for the hour plus quick minute buttons. Nothing reaches
 * the parent via onChange until "Use this date and time" is pressed.
 */
const DateTimePickerCal = ({ value, onChange, isInvalid, placeholder = 'Pick a date and time' }) => {
  const today = new Date();
  const triggerRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(value ? value.getFullYear() : today.getFullYear());
  const [viewMonth, setViewMonth] = useState(value ? value.getMonth() : today.getMonth());
  const [pickedYear, setPickedYear] = useState(value ? value.getFullYear() : null);
  const [pickedMonth, setPickedMonth] = useState(value ? value.getMonth() : null);
  const [pickedDay, setPickedDay] = useState(value ? value.getDate() : null);
  const [hour12, setHour12] = useState(value ? to12Hour(value.getHours()) : null);
  const [minute, setMinute] = useState(value ? value.getMinutes() : null);
  const [meridiem, setMeridiem] = useState(value && value.getHours() >= 12 ? 'PM' : 'AM');

  // Re-sync internal state whenever the confirmed value changes from outside
  // (e.g. the form switches to a different practice).
  useEffect(() => {
    setViewYear(value ? value.getFullYear() : today.getFullYear());
    setViewMonth(value ? value.getMonth() : today.getMonth());
    setPickedYear(value ? value.getFullYear() : null);
    setPickedMonth(value ? value.getMonth() : null);
    setPickedDay(value ? value.getDate() : null);
    setHour12(value ? to12Hour(value.getHours()) : null);
    setMinute(value ? value.getMinutes() : null);
    setMeridiem(value && value.getHours() >= 12 ? 'PM' : 'AM');
    setOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value ? value.getTime() : null]);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const pickDay = (d) => {
    setPickedYear(viewYear);
    setPickedMonth(viewMonth);
    setPickedDay(d);
  };

  const confirm = () => {
    if (pickedDay === null || hour12 === null) return;
    const finalMinute = minute === null ? 0 : minute;
    const hour24 = (hour12 % 12) + (meridiem === 'PM' ? 12 : 0);
    onChange(new Date(pickedYear, pickedMonth, pickedDay, hour24, finalMinute, 0, 0));
    setOpen(false);
  };

  const dayCells = buildDayCells(viewYear, viewMonth);

  return (
    <div className="dtpc">
      <button
        ref={triggerRef}
        type="button"
        className={`form-control dtpc-trigger${isInvalid ? ' is-invalid' : ''}${value ? ' border-success' : ''}`}
        onClick={() => setOpen(o => !o)}
      >
        {formatTrigger(value) || placeholder}
      </button>

      <Collapse
        in={open}
        onExited={() => triggerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
      >
        <div className="dtpc-panel">
          <div className="dtpc-cal-head">
            <button type="button" className="dtpc-nav" onClick={prevMonth} aria-label="Previous month">
              <FaChevronLeft />
            </button>
            <span>{MONTH_NAMES[viewMonth]} {viewYear}</span>
            <button type="button" className="dtpc-nav" onClick={nextMonth} aria-label="Next month">
              <FaChevronRight />
            </button>
          </div>

          <div className="dtpc-dow">
            {DOW_LABELS.map((d, i) => <span key={i}>{d}</span>)}
          </div>

          <div className="dtpc-grid">
            {dayCells.map((d, i) => {
              if (d === null) return <span key={i} className="dtpc-cell dtpc-cell--empty" />;

              const isToday = today.getFullYear() === viewYear && today.getMonth() === viewMonth && today.getDate() === d;
              const isSelected = pickedYear === viewYear && pickedMonth === viewMonth && pickedDay === d;

              return (
                <button
                  key={i}
                  type="button"
                  className={`dtpc-cell${isToday ? ' dtpc-cell--today' : ''}${isSelected ? ' dtpc-cell--selected' : ''}`}
                  onClick={() => pickDay(d)}
                >
                  {d}
                </button>
              );
            })}
          </div>

          <hr className="dtpc-divider" />

          <p className="dtpc-readout">
            {hour12 ?? '--'}:{minute !== null ? pad2(minute) : '--'} {meridiem}
          </p>

          <div className="dtpc-meridiem">
            <button
              type="button"
              className={`dtpc-meridiem-btn${meridiem === 'AM' ? ' active' : ''}`}
              onClick={() => setMeridiem('AM')}
            >
              AM
            </button>
            <button
              type="button"
              className={`dtpc-meridiem-btn${meridiem === 'PM' ? ' active' : ''}`}
              onClick={() => setMeridiem('PM')}
            >
              PM
            </button>
          </div>

          <div className="dtpc-dial">
            <span className="dtpc-dial-center" />
            {hour12 !== null && (
              <div
                className="dtpc-dial-hand"
                style={{ transform: `rotate(${(hour12 === 12 ? 0 : hour12) * 30}deg)` }}
              />
            )}
            {CLOCK_POSITIONS.map(({ h, left, top }) => (
              <button
                key={h}
                type="button"
                className={`dtpc-dial-num${hour12 === h ? ' selected' : ''}`}
                style={{ left, top }}
                onClick={() => setHour12(h)}
              >
                {h}
              </button>
            ))}
          </div>

          <div className="dtpc-minutes">
            <span className="dtpc-minutes-label">Minutes</span>
            {MINUTE_OPTIONS.map(m => (
              <button
                key={m}
                type="button"
                className={`dtpc-minute-btn${minute === m ? ' active' : ''}`}
                onClick={() => setMinute(m)}
              >
                {pad2(m)}
              </button>
            ))}
          </div>

          <button
            type="button"
            className="btn btn-sm btn-outline-success"
            disabled={pickedDay === null || hour12 === null}
            onClick={confirm}
          >
            Use this date and time
          </button>
        </div>
      </Collapse>
    </div>
  );
};

export default DateTimePickerCal;
